/* tslint:disable:variable-name - allow variables that begin with an underscore */

import {htmlEscape} from "@/shared/utils/string";
import {formatPercent, mergeProperties, setSwitcheryState, toggleSwitchery} from "@/shared/utils/misc";
import {formatDecimalNumber} from "@/shared/utils/datefunctions";
import {FeatureModule, FeatureModuleDiscountResult, MarketingText} from "@/shared/timr";

export interface FeatureModuleItemBuildOptions {
	switcheryClass?: string;
	showFeatureViewOnInitialization?: boolean;
	disableSwitchIf?: (fm: FeatureModule) => boolean;
	popover?: (dom: HTMLElement) => void;
}

/**
 * Custom web component that draws the content of the feature module selection dialog.
 */
export class FeatureModuleItem extends HTMLElement {
	private static readonly DEFAULT_SWITCHERY_CLASS: string = "switchery-xs";

	public static readonly EVENT_BASE_FEATURE_MODULE_SELECTED = "pricing_feature_module_selection_base_selected";
	public static readonly EVENT_FEATURE_MODULE_TOGGLED = "pricing_feature_module_selected";
	public static readonly EVENT_UPDATE_ADDONS = "pricing_update_addons";

	private _featureModule: FeatureModule;
	private _dependsOn: FeatureModuleItem = null;
	private _buildOptions: FeatureModuleItemBuildOptions = {
		switcheryClass: FeatureModuleItem.DEFAULT_SWITCHERY_CLASS,
		showFeatureViewOnInitialization: true,
		disableSwitchIf: () => false,
		popover: () => {
		}
	};

	private featureList: JQuery;
	private chevron: HTMLElement;

	get featureModule(): FeatureModule {
		return this._featureModule;
	}

	set featureModule(value: FeatureModule) {
		this._featureModule = value;
	}

	get dependsOn(): FeatureModuleItem {
		return this._dependsOn;
	}

	set dependsOn(value: FeatureModuleItem) {
		this._dependsOn = value;
	}

	set buildOptions(value: FeatureModuleItemBuildOptions) {
		this._buildOptions = mergeProperties(this._buildOptions, value);
	}

	public connectedCallback() {
		this.buildItem(this.featureModule);
	}

	public toggleSwitcheryOnItem(enabled: boolean, includeAddOns: boolean = false): void {
		const checkBox: HTMLInputElement = this.querySelector("[data-checkbox]");
		if (enabled) {
			toggleSwitchery(checkBox, this._buildOptions.disableSwitchIf(this.featureModule), false);
		} else {
			toggleSwitchery(checkBox, true, true);
		}
		this.updateFeatureListColor();
		if (includeAddOns) {
			const addons = this.querySelectorAll("[data-addons] > feature-module-item");
			for (const addon of addons ?? []) {
				const addonItem = addon as FeatureModuleItem;
				addonItem.toggleSwitcheryOnItem(enabled, includeAddOns);
			}
		}
	}

	public setSwitcheryStateOnItem(checked: boolean, includeAddOns: boolean = false): void {
		const checkBox = this.querySelector("[data-checkbox]");
		setSwitcheryState(checkBox, checked);
		this.updateFeatureListColor();
		if (includeAddOns) {
			const addons = this.querySelectorAll("[data-addons] > feature-module-item");
			for (const addon of addons ?? []) {
				const addonItem = addon as FeatureModuleItem;
				addonItem.setSwitcheryStateOnItem(checked, includeAddOns);
			}
		}
	}

	public isEveryDependencyActive() {
		return this.isEveryDependencyActiveInternal(this.dependsOn);
	}

	public isActive(): boolean {
		return $(this).find("[data-checkbox]").first().is(":checked");
	}

	public isIndividualOnly(): boolean {
		return this.featureModule.individualOnly;
	}

	public getSelectedModules(): FeatureModule[] {
		const selectedModules: FeatureModule[] = [];
		if (this.isActive()) {
			selectedModules.push(this.featureModule);
		}
		const addons = this.querySelectorAll("[data-addons] > feature-module-item");
		for (const addon of addons ?? []) {
			const addonItem = addon as FeatureModuleItem;
			selectedModules.push(...addonItem.getSelectedModules());
		}
		return selectedModules;
	}

	public updatePrice(basePrice: number, pricePerUser: number): void {
		this.querySelector("[data-feature-module-price]").innerHTML =
			this.formatPrice(basePrice, pricePerUser, this.featureModule.currency)
	}

	private buildItem(featureModule: FeatureModule): void {
		this.innerHTML = `
				<div>
					<div class="panel panel-default panel-feature-module-item p-10">
						<div class="flex-row justify-content-space-between">
							<div class="flex-column-auto">
								<div class="flex-row-lg">
									<div class="flex-column-auto" style="display: inline">
										<label class="h4 m-0">
											<span data-name class="text-nowrap">
												${featureModule.icon ? `<i class="${this.parseIconDefinition(featureModule.icon)} position-left"></i>` : ""}
												${htmlEscape(featureModule.name)}
											</span>
										</label>
									</div>
									<div class="flex-column ml-15" data-feature-module-price>
										${this.formatPrice(featureModule.basePrice, featureModule.pricePerUser, featureModule.currency)}
									</div>
								</div>
								<div class="flex-row-lg-reverse">
									<div class="flex-column-auto module-description mt-15">
										<span data-description>${featureModule.description ? htmlEscape(featureModule.description) : ""}</span>
									</div>
									<div class="flex-column w-auto justify-content-end text-right text-muted ml-80 mb-10 flex-row pr-5" data-module-discount-info>
										<span class="text-nowrap"></span><i class="far fa-info-circle position-right text-aligned"></i>
									</div>
								</div>
							</div>
							<div class="flex-column">
								<label class="checkbox checkbox-switchery ${this._buildOptions.switcheryClass} switchery-double display h-100">
									<input data-checkbox type="checkbox" class="switchery" data-switchery="true" />
								</label>
							</div>
						</div>
						<div data-drawer class="flex-row module-show-details-drawer">
							<i data-chevron class="fas fa-caret-right position-left" style="width: 9px"></i>
							<span>${messages[`pricing.featureModuleItem.showDetails${this.featureModule.addOns ? ".addons" : ""}`]}</span>
						</div>
						<div data-feature-view class="flex-row-lg mt-5">
							<div class="flex-column-40 mr-20" data-features>
							</div>
							<div data-addons class="flex-column-60 addon-view"></div>
						</div>
					</div>
				</div>
			`;

		this.featureList = $(this).find("[data-feature-view]").first();
		this.chevron = this.querySelector("[data-chevron]");

		const checkBox = $(this).find("[data-checkbox]").first();
		checkBox.prop("checked", featureModule.active);

		// Init switchery first time
		new Switchery(this.querySelector("[data-checkbox]"));

		if (featureModule.active && this._buildOptions.showFeatureViewOnInitialization) {
			this.showFeatureView();
		} else {
			this.hideFeatureView();
		}
		this.querySelector("[data-drawer]").addEventListener("click", (e) => {
			e.stopPropagation();
			this.toggleFeatureView();
		});

		checkBox.change((e) => {
			e.stopPropagation();
			if ($(e.target).is(":checked")) {
				this.showFeatureView();
			}
			this.toggleAddons();
			this.updateFeatureListColor();
			this.fireFeatureModuleToggledEvent();
		});

		this.drawFeatureList(featureModule);
		this.drawAddOns(featureModule);

		this.toggleSwitcheryOnItem(this.isEveryDependencyActive());
		this._buildOptions.popover(this);
	}

	private drawFeatureList(featureModule: FeatureModule) {
		const featureList = this.querySelector("[data-features]");
		if (featureModule.marketingTexts) {
			const marketingTextNodes = featureModule.marketingTexts.map(marketingText => this.buildMarketingText(marketingText));
			featureList.replaceChildren(...marketingTextNodes);
		} else {
			const ulElement = document.createElement("ul");
			ulElement.classList.add("module-selector-feature-list");
			for (const feature of featureModule.features ?? []) {
				const li = document.createElement("li");
				li.innerHTML = messages["pricing.timrFeature." + feature.name];
				ulElement.appendChild(li);
			}
			featureList.replaceChildren(ulElement);
		}
	}

	private drawAddOns(featureModule: FeatureModule) {
		const addonItems: FeatureModuleItem[] = [];
		for (const addon of featureModule.addOns ?? []) {
			const addonItem = document.createElement("feature-module-item") as FeatureModuleItem;
			addonItem.featureModule = addon;
			addonItem.buildOptions = this._buildOptions;
			addonItem.addEventListener(FeatureModuleItem.EVENT_UPDATE_ADDONS, () => this.updateAddonView());
			addonItem.addEventListener(FeatureModuleItem.EVENT_FEATURE_MODULE_TOGGLED, this.passEvent);
			addonItems.push(addonItem);
		}
		if (addonItems.length > 0) {
			// Set dependsOn for all addons based on their featureModule, addons can be dependent on another addon
			// and not only on the base feature module
			addonItems.forEach(addon => {
				const depOn = addon.featureModule.dependsOn;
				if (depOn) {
					let dependency: FeatureModuleItem;
					if (depOn.id === this.featureModule.id) {
						// eslint-disable-next-line @typescript-eslint/no-this-alias
						dependency = this;
					} else {
						dependency = addonItems.find(a => a.featureModule.id === depOn.id);
					}
					addon.dependsOn = dependency;
				}
			});

			this.querySelector("[data-addons]").replaceChildren(...addonItems);
		} else {
			$(this).find("[data-addons]").hide();
		}
	}

	private toggleAddons(): void {
		if (!this.featureModule.dependsOn) {
			this.dispatchEvent(new CustomEvent(FeatureModuleItem.EVENT_BASE_FEATURE_MODULE_SELECTED, {
				detail: {
					baseFeatureModule: this.featureModule,
					active: this.isActive()
				}
			}));
			this.updateAddonView();
		} else {
			this.dispatchEvent(new CustomEvent(FeatureModuleItem.EVENT_UPDATE_ADDONS));
		}
	}

	private isEveryDependencyActiveInternal(featureModule: FeatureModuleItem): boolean {
		if (featureModule === null) {
			return true;
		}
		if (!featureModule.isActive()) {
			return false;
		}
		return this.isEveryDependencyActiveInternal(featureModule.dependsOn);
	}

	private updateAddonView() {
		const addons = this.querySelectorAll("[data-addons] > feature-module-item");
		for (const addon of addons ?? []) {
			const addonItem = addon as FeatureModuleItem;
			addonItem.toggleSwitcheryOnItem(addonItem.isEveryDependencyActive());
		}
	}

	/**
	 * Toggles the visibility of the feature list.
	 * The feature list has to be displayed as a flexbox container to be able to show the list items in a column.
	 * @private
	 */
	private toggleFeatureView() {
		if (this.featureList.is(":visible")) {
			this.hideFeatureView();
		} else {
			this.showFeatureView();
		}
		this.updateFeatureListColor();
	}

	private hideFeatureView() {
		this.featureList.slideToggleBool(false);
		this.chevron.classList.replace("fa-caret-down", "fa-caret-right");

	}

	private showFeatureView() {
		this.featureList.slideToggleBool(true);
		this.chevron.classList.replace("fa-caret-right", "fa-caret-down");
	}

	private updateFeatureListColor() {
		$(this).find("[data-features]").first().toggleClass("active", this.isActive());
		$(this).find(".panel").first().toggleClass("active", this.isActive());
	}

	private fireFeatureModuleToggledEvent() {
		return this.dispatchEvent(new CustomEvent(FeatureModuleItem.EVENT_FEATURE_MODULE_TOGGLED));
	}

	/**
	 * Dispatches a CustomEvent to pass it to the next "higher" layer
	 * In it's used to pass it to the dependency feature module
	 */
	private readonly passEvent = (e: Event) => this.dispatchEvent(new CustomEvent(e.type, e));

	private formatPrice(basePrice: number, pricePerUser: number, currency: string): string {
		if (basePrice !== undefined && basePrice > 0 && pricePerUser !== undefined && pricePerUser > 0) {
			return `
					<div class="price-info-container">
						<span data-price class="data-text-large m-0">${formatDecimalNumber(basePrice)}</span>
						<span class="price-info text-muted">${currency} ${messages["pricing.priceInfo.basePrice"]}</span>
						<span data-price class="data-text-large m-0">+ ${formatDecimalNumber(pricePerUser)}</span>
						<span class="price-info text-muted mr-15">${currency} ${messages["pricing.priceInfo.plusPerUser"]}</span>
					</div>
				`;
		} else if (basePrice !== undefined && basePrice > 0 && (pricePerUser === undefined || pricePerUser == 0)) {
			return `
					<div class="price-info-container">
						<span data-price class="data-text-large m-0">${formatDecimalNumber(basePrice)}</span>
						<span class="price-info text-muted mr-15">${currency} ${messages["pricing.priceInfo.basePrice"]}</span>
					</div>
				`;
		} else {
			return `
					<div class="price-info-container">
						<span data-price class="data-text-large m-0">${formatDecimalNumber(pricePerUser)}</span>
						<span class="price-info text-muted mr-15">${currency} ${messages["pricing.priceInfo.plusPerUser"]}</span>
					</div>
				`;
		}
	}

	private buildMarketingText(marketingText: MarketingText): HTMLElement {
		const div = document.createElement("div");
		const heading = document.createElement("h4");
		heading.classList.add("marketing-text-heading");
		heading.textContent = marketingText.group;

		// build the list of features
		const ul = document.createElement("ul");
		ul.classList.add("module-selector-feature-list");
		for (const feature of marketingText.features) {
			const li = document.createElement("li");
			li.append(feature.name);
			if (feature.tooltip) {
				const tooltip = document.createElement("i");
				tooltip.classList.add("fal", "fa-info-circle", "position-right");

				$(tooltip).popover({
					trigger: "hover",
					container: "body",
					delay: {show: 500, hide: 0},
					placement: "right",
					html: false,
					template: `<div class="popover" role="tooltip">
								<div class="arrow"></div>
								<div class="popover-content"></div>
							</div>`,
					content: feature.tooltip
				});

				li.append(tooltip);
			}
			ul.appendChild(li);
		}

		div.appendChild(heading);
		div.appendChild(ul);
		return div;
	}

	public updateDiscountInfo(featureModuleDiscountResult?: FeatureModuleDiscountResult) {
		const discountInfo: HTMLElement = this.querySelector("[data-module-discount-info]");
		if (!featureModuleDiscountResult) {
			discountInfo.style.visibility = "hidden";
		} else {
			const discountInfoText = discountInfo.querySelector("span");
			discountInfoText.textContent = messages.withArguments(
				"pricing.priceInfo.lowestPossiblePrice",
				`${formatDecimalNumber(featureModuleDiscountResult.lowestPossiblePrice)} ${this.featureModule.currency}`
			);

			const discountInfoPopoverTarget = discountInfo.querySelector("i");
			$(discountInfoPopoverTarget).popover("destroy").popover({
				container: "body",
				placement: "left",
				trigger: "hover",
				delay: {show: 400, hide: 0},
				html: true,
				template: `<div class="popover" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>`,
				content: () => {
					if (featureModuleDiscountResult.percentageDiscount) {
						return messages.withArguments(
							`pricing.priceInfo.discountType.${featureModuleDiscountResult.moduleDiscountType}`,
							formatPercent(featureModuleDiscountResult.percentageDiscount),
							featureModuleDiscountResult.requiredModulesCount
						);
					} else {
						return messages.withArguments(
							`pricing.priceInfo.discountType.${featureModuleDiscountResult.moduleDiscountType}`,
							`${formatDecimalNumber(featureModuleDiscountResult.totalDiscount)} ${this.featureModule.currency}`,
							featureModuleDiscountResult.requiredModulesCount
						);
					}
				}
			})

			discountInfo.style.visibility = "visible";
		}
	}

	/**
	 * Parses the icon definition string to extract Font Awesome style and class name
	 * @param iconDefinition - String containing Font Awesome icon definition (e.g., "fas fa-user", "fa-user", "user")
	 * @returns {string} - Parsed icon class string (e.g., "user" -> "far fa-user")
	 */
	private parseIconDefinition(iconDefinition?: string): string {
		if (!iconDefinition) {
			return "";
		}

		const iconParts = iconDefinition.split(" ");

		// Determine icon style (fas, far, fal, fat, fab) - default to "far" if not specified
		const iconStyle = iconParts[0].match("^(fa[srltb])") ? iconParts[0] : "far";

		// Determine icon class (fa-user, fa-chart, etc.)
		let iconClass: string;

		if (iconParts.length > 1) {
			// If multiple parts, second part should be the icon class
			iconClass = iconParts[1];
		} else {
			// If only one part, check if it already has fa- prefix
			iconClass = iconParts[0].startsWith("fa-") ? iconParts[0] : `fa-${iconParts[0]}`;
		}

		return `${iconStyle} ${iconClass}`;
	}

}
